import { AHThemeType, ColorType, useTheme } from "../AHTheme/AHTheme";
import { Layout, TextSize, useLayoutContext } from "../LayoutProvider";
import {
  ColorValue,
  PixelRatio,
  Platform,
  StyleProp,
  Text,
  TextProps,
  TextStyle,
} from "react-native";
import React, { useMemo } from "react";

import { BoxProps } from "../AHTheme/BoxProps";
import { useBoxStyle } from "../AHTheme/useBoxStyle";
import { useScaleFactor } from "../AHScaleFactor";
import { AHTextFonts } from "./AHTextFonts";
import Color from "color";

export { AHTextFonts };
export const resolveAHTextStyle = (
  type: TextTypes | undefined,
  scaleFactor: number,
  theme: AHThemeType,
  layout: Layout
): Partial<TextStyle> => {
  switch (type) {
    case "list-item-title":
      return {
        fontSize: PixelRatio.roundToNearestPixel(
          layout.text.smallTitle * scaleFactor
        ),
        fontFamily: AHTextFonts.REGULAR,
        color: theme["text-primary"],
      };
    case "list-item-title-selected":
      return {
        fontSize: PixelRatio.roundToNearestPixel(
          layout.text.smallTitle * scaleFactor
        ),
        fontFamily: AHTextFonts.BOLDER,
        color: theme["text-primary"],
      };

    case "list-item-title-secondary":
      return {
        fontSize: PixelRatio.roundToNearestPixel(
          layout.text.body * scaleFactor
        ),
        fontFamily: AHTextFonts.BOLD,
        color: theme["text-secondary"],
      };
    case "body-big":
      return {
        fontSize: PixelRatio.roundToNearestPixel(
          layout.text.body * scaleFactor
        ),
        fontFamily: AHTextFonts.REGULAR,
        color: theme["text-primary"],
      };
    case "body":
      return {
        fontSize: PixelRatio.roundToNearestPixel(
          layout.text.body * scaleFactor
        ),
        fontFamily: AHTextFonts.REGULAR,
        color: theme["text-secondary"],
      };
    case "subtitle":
      return {
        fontSize: PixelRatio.roundToNearestPixel(
          layout.text.body * scaleFactor
        ),
        fontFamily: AHTextFonts.BOLD,
        color: theme["text-primary"],
      };
    case "stat-number-large":
      return {
        fontSize: PixelRatio.roundToNearestPixel(
          layout.text.xxlargeTitle * scaleFactor
        ),
        fontFamily: AHTextFonts.BOLDER,
        lineHeight: Platform.OS === "android" ? 35 : undefined,
        color: theme["text-primary"],
      };
    case "stat-number":
      return {
        fontSize: PixelRatio.roundToNearestPixel(
          layout.text.xlargeTitle * scaleFactor
        ),
        fontFamily: AHTextFonts.BOLDER,
        lineHeight: Platform.OS === "android" ? 35 : undefined,
        color: theme["text-primary"],
      };
    case "title":
      return {
        fontSize: PixelRatio.roundToNearestPixel(
          layout.text.title * scaleFactor
        ),
        fontFamily: AHTextFonts.BOLD,
        color: theme["text-primary"],
      };
    case "title-big":
      return {
        fontSize: PixelRatio.roundToNearestPixel(
          layout.text.largeTitle * scaleFactor
        ),
        fontFamily: AHTextFonts.BOLDER,
        color: theme["text-primary"],
      };
    case "title-small-regular":
      return {
        fontSize: PixelRatio.roundToNearestPixel(
          layout.text.smallTitle * scaleFactor
        ),
        fontFamily: AHTextFonts.REGULAR,
        color: theme["text-primary"],
      };
    case "title-small":
      return {
        fontSize: PixelRatio.roundToNearestPixel(
          layout.text.smallTitle * scaleFactor
        ),
        fontFamily: AHTextFonts.BOLD,
        color: theme["text-primary"],
      };

    case "detail":
      return {
        fontSize: PixelRatio.roundToNearestPixel(
          layout.text.detail * scaleFactor
        ),
        fontFamily: AHTextFonts.MEDIUM,
        color: theme["text-washed"],
      };
    case "info-small":
      return {
        fontSize: PixelRatio.roundToNearestPixel(
          layout.text.detail * scaleFactor
        ),
        fontFamily: AHTextFonts.BOLD,
        color: theme["text-primary"],
      };
    default:
      return {
        fontSize: PixelRatio.roundToNearestPixel(
          layout.text.body * scaleFactor
        ),
        color: theme["text-secondary"],
        fontFamily: AHTextFonts.REGULAR,
      };
  }
};

export type TextTypes =
  | "stat-number-large"
  | "stat-number"
  | "title"
  | "title-big"
  | "title-small"
  | "list-item-title"
  | "list-item-title-secondary"
  | "list-item-title-selected"
  | "body-big"
  | "body"
  | "subtitle"
  | "detail"
  | "info-small"
  | "title-small-regular";

export type AHTextProps = {
  type?: TextTypes;
  font?: AHTextFonts;
  size?: number | TextSize;
  washed?: boolean;
  children: React.ReactNode;
  textAlign?: TextStyle["textAlign"];
} & TextProps &
  ColorProps &
  Omit<BoxProps, "color">;

type ColorProps = {
  color?: ColorType | (string & {});
  washed?: boolean;
  darkenColor?: number;
  lightenColor?: number;
};
function useColor(
  {
    color,
    washed,
    darkenColor,
    lightenColor,
    defaultColor,
  }: ColorProps & { defaultColor?: ColorValue },
  theme: AHThemeType
) {
  return useMemo(() => {
    if (washed) {
      return theme.washedText;
    }
    const selectedColor = color
      ? (theme[color as ColorType] ?? color)
      : (defaultColor ?? theme["text-primary"]);

    if (darkenColor) {
      return Color(selectedColor).darken(darkenColor).hex();
    }
    if (lightenColor) {
      return Color(selectedColor).lighten(lightenColor).hex();
    }

    return selectedColor;
  }, [color, darkenColor, defaultColor, lightenColor, theme, washed]);
}
const AHTextCmp = ({
  style,
  type,
  washed,
  size,
  font,
  color,
  darkenColor,
  lightenColor,
  textAlign,
  children,
  ...props
}: AHTextProps) => {
  const { theme, sf, layout, defaultStyle } = useAHTextTypeStyles(type);

  const selectedColor = useColor(
    {
      color,
      washed,
      darkenColor,
      lightenColor,
      defaultColor: defaultStyle.color,
    },
    theme
  );

  const boxStyle = useBoxStyle(props);
  const calcStyle = useMemo(() => {
    const s: StyleProp<TextStyle> = {
      color: selectedColor,
    };

    if (textAlign) {
      s.textAlign = textAlign;
    }
    if (font) {
      s.fontFamily = font;
    }
    if (size) {
      if (typeof size === "number") {
        s.fontSize = PixelRatio.roundToNearestPixel(size * sf);
      } else {
        s.fontSize = PixelRatio.roundToNearestPixel(layout.text[size] * sf);
      }
    }

    return s;
  }, [selectedColor, font, layout.text, sf, size, textAlign]);

  const styles = useMemo(
    () => [boxStyle, defaultStyle, calcStyle, style],
    [boxStyle, defaultStyle, calcStyle, style]
  );
  return (
    <Text {...props} allowFontScaling={false} style={styles}>
      {children}
    </Text>
  );
};

export const AHText = React.memo(AHTextCmp);

export function useAHTextTypeStyles(type: TextTypes | undefined) {
  const theme = useTheme();
  const layout = useLayoutContext();
  const sf = useScaleFactor();
  const defaultStyle = useMemo(
    () => resolveAHTextStyle(type, sf, theme, layout),
    [layout, sf, theme, type]
  );
  return { theme, sf, layout, defaultStyle };
}
